import {
  prisma,
  OrderStatus,
  PaymentStatus,
  InvoiceStatus,
  Currency,
  PaymentMethod,
} from "client";

export type InvoiceTicketLine = {
  /** ticketId, or "other" when the purchased item is not linked to a ticket */
  id: string;
  name: string;
  quantity: number;
  unitPriceMinor: number;
  totalMinor: number;
  orderItemIds: string[];
};

export type InvoiceCustomer = {
  name: string | null;
  email: string | null;
  phoneNumber: string | null;
  company: string | null;
};

export type InvoiceEventInfo = {
  id: string;
  number: number;
  name: string | null;
  startDate: Date | null;
  endDate: Date | null;
  timezone: string;
  logo: string | null;
};

export type InvoicePaymentInfo = {
  id: string;
  number: number;
  status: PaymentStatus;
  method: PaymentMethod | null;
  grandTotalMinor: number | null;
  currency: Currency;
  createdAt: Date;
};

export type InvoiceDocumentInfo = {
  number: number;
  status: InvoiceStatus;
  totalMinor: number;
  subTotalMinor: number | null;
  taxMinor: number | null;
} | null;

export type InvoiceOrganizationInfo = {
  name: string;
  logo: string | null;
  email: string | null;
  phoneNumber: string | null;
  address: string | null;
  slug: string;
};
export type InvoiceOrderData = {
  orderId: string;
  orderNumber: number;
  status: OrderStatus;
  currency: Currency;
  createdAt: Date;
  completedAt: Date | null;
  subTotalMinor: number;
  taxMinor: number;
  totalMinor: number;
  customer: InvoiceCustomer | null;
  event: InvoiceEventInfo;
  tickets: InvoiceTicketLine[];
  payments: InvoicePaymentInfo[];
  paymentStatus: PaymentStatus | "UNPAID";
  invoice: InvoiceDocumentInfo;
  organization: InvoiceOrganizationInfo;
};

/**
 * Fetches every relation required to render an order invoice
 * (customer, event, ticket lines, payments, invoice record) in a single
 * round trip, then shapes the result into a flat, presentation-friendly
 * structure so rendering components never need to know about the Prisma
 * schema.
 */
export const getOrderInvoiceData = async (
  orderId: string,
  lang?: string
): Promise<InvoiceOrderData | null> => {
  if (!orderId) {
    throw new Error("orderId is required to build invoice data");
  }

  try {
    const order = await prisma.order.findUnique({
      where: { id: orderId },
      include: {
        profile: true,
        invoices: {
          orderBy: { createdAt: "desc" },
          take: 1,
        },
        payments: {
          orderBy: { createdAt: "desc" },
          include: { paymentMethod: true },
        },
        event: {
          include: {
            translations: lang
              ? { where: { language: { code: lang } }, take: 1 }
              : { take: 1 },
            whiteLabel: { select: { name: true, image: true } },
            organizations: {
              select: {
                translations: lang
                  ? { where: { language: { code: lang } }, take: 1 }
                  : { take: 1 },
                email: true,
                phoneNumber: true,
                address: true,
                slug: true,
                branding: { select: { logo: true } },
              },
            },
          },
        },
        orderItems: {
          orderBy: { createdAt: "asc" },
          include: {
            ticket: {
              include: {
                bookableItem: {
                  include: {
                    translations: lang
                      ? { where: { language: { code: lang } }, take: 1 }
                      : { take: 1 },
                  },
                },
              },
            },
          },
        },
      },
    });

    if (!order) return null;

    const ticketLineByKey = new Map<string, InvoiceTicketLine>();

    for (const item of order.orderItems) {
      const bookableItem = item.ticket?.bookableItem;
      const translation = bookableItem?.translations?.[0];
      const key = item.ticketId ?? "other";
      const totalMinor = item.total ?? 0;
      const existing = ticketLineByKey.get(key);

      if (existing) {
        existing.quantity += 1;
        existing.totalMinor += totalMinor;
        existing.orderItemIds.push(item.id);
      } else {
        ticketLineByKey.set(key, {
          id: key,
          name: translation?.name ?? (item.ticketId ? "Ticket" : "Other"),
          quantity: 1,
          unitPriceMinor: totalMinor,
          totalMinor,
          orderItemIds: [item.id],
        });
      }
    }

    const tickets = Array.from(ticketLineByKey.values()).map((line) => ({
      ...line,
      unitPriceMinor:
        line.quantity > 0
          ? Math.round(line.totalMinor / line.quantity)
          : line.unitPriceMinor,
    }));

    const payments: InvoicePaymentInfo[] = order.payments.map((payment) => ({
      id: payment.id,
      number: payment.number,
      status: payment.status,
      method: payment.paymentMethod?.type ?? null,
      grandTotalMinor: payment.grandTotal ?? null,
      currency: payment.currency ?? order.currency,
      createdAt: payment.createdAt,
    }));

    const paidPayment = order.payments.find(
      (payment) => payment.status === PaymentStatus.PAID
    );
    const paymentStatus: PaymentStatus | "UNPAID" =
      paidPayment?.status ?? order.payments[0]?.status ?? "UNPAID";

    const eventTranslation = order.event.translations?.[0];
    const event: InvoiceEventInfo = {
      id: order.event.id,
      number: order.event.number,
      name: eventTranslation?.name ?? null,
      startDate: order.event.startDate,
      endDate: order.event.endDate,
      timezone: order.event.timezone,
      logo: order.event.whiteLabel?.image ?? null,
    };
    const organization = {
      ...order.event.organizations[0],
      name:
        order.event.organizations[0]?.translations?.[0]?.name ??
        order.event.organizations[0]?.slug,
      logo: order.event.organizations[0]?.branding?.logo ?? null,
    };

    const customer: InvoiceCustomer | null = order.profile
      ? {
          name: order.profile.name ?? null,
          email: order.profile.email ?? null,
          phoneNumber: order.profile.phoneNumber ?? null,
          company: order.profile.company ?? null,
        }
      : null;

    const invoiceRecord = order.invoices[0];
    const invoice: InvoiceDocumentInfo = invoiceRecord
      ? {
          number: invoiceRecord.number,
          status: invoiceRecord.status,
          totalMinor: invoiceRecord.total,
          subTotalMinor: invoiceRecord.subTotal ?? null,
          taxMinor: invoiceRecord.tax ?? null,
        }
      : null;

    const subTotalMinor =
      order.subTotal ?? tickets.reduce((sum, line) => sum + line.totalMinor, 0);
    const taxMinor = order.tax ?? 0;
    const totalMinor = order.total ?? subTotalMinor + taxMinor;

    return {
      orderId: order.id,
      orderNumber: order.number,
      status: order.status,
      currency: order.currency,
      createdAt: order.createdAt,
      completedAt: order.completedAt,
      subTotalMinor,
      taxMinor,
      totalMinor,
      customer,
      event,
      organization,
      tickets,
      payments,
      paymentStatus,
      invoice,
    };
  } catch (error) {
    throw error;
  }
};
